import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useLanguage } from "@/contexts/LanguageContext";
import { mediaUrl, openMedia } from "@/lib/mediaUrl";
import {
  User2,
  Building2,
  CreditCard as CardIcon,
  CalendarDays,
  FileText,
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Eye,
  Bell,
} from "lucide-react";
import { toast } from "sonner";
import {
  getInvoice,
  verifyPayment,
  getInvoicePaymentDetails,
  getBCVRate,
  downloadInvoice,
  sendOverdueReminder,
  resendNotification,
  type Invoice,
  type InvoiceStatus,
  type InvoiceNotification,
  type VerifyPaymentData,
  type InvoicePaymentDetails,
} from "@/api/payments";
import { CommissionBreakdown } from "@/components/CommissionBreakdown";
import { PaymentDetailsCard } from "@/components/PaymentDetailsCard";

const PRIMARY = "#1F3A93";
const QUIET =
  "!bg-white !text-gray-900 !border-black/10 border ring-1 ring-black/5 shadow-sm rounded-xl hover:!bg-gray-50 active:!bg-gray-50";

function formatDate(
  dateStr: string | undefined,
  locale: string,
  naLabel: string,
): string {
  if (!dateStr) return naLabel;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatCurrency(amount: string | number): string {
  const num = typeof amount === "string" ? parseFloat(amount) : amount;
  return `$${num.toFixed(2)}`;
}

export default function PaymentDetailsPage() {
  const { t, language } = useLanguage();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dateLocale = language === "es" ? "es-ES" : "en-US";

  const [verificationNotes, setVerificationNotes] = useState("");
  const [confirmationFile, setConfirmationFile] = useState<File | null>(null);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);

  // BCV rate for dual-currency display
  const bcvRateQuery = useQuery({
    queryKey: ["bcv-rate"],
    queryFn: () => getBCVRate(),
    staleTime: 1000 * 60 * 30,
  });
  const bcvRate = bcvRateQuery.data?.dollar;

  // Fetch invoice details
  const {
    data: invoice,
    isLoading,
    error,
  } = useQuery<Invoice>({
    queryKey: ["invoice-detail", id],
    queryFn: () => getInvoice(id!),
    enabled: !!id,
  });

  // Fetch payment details (platform and HOA)
  const { data: paymentDetails } = useQuery<InvoicePaymentDetails>({
    queryKey: ["invoice-payment-details", id],
    queryFn: () => getInvoicePaymentDetails(id!),
    enabled: !!id && !!invoice,
  });

  // Verify payment mutation
  const verifyMutation = useMutation({
    mutationFn: (payload: VerifyPaymentData & { invoiceId: string }) =>
      verifyPayment(payload.invoiceId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["pending-verification-invoices"],
      });
      queryClient.invalidateQueries({ queryKey: ["invoice-detail", id] });
      toast.success(t("superAdmin.paymentDetail.toasts.verifySuccess"));
      navigate("/super-admin/payments");
    },
    onError: (err: any) => {
      toast.error(t("superAdmin.paymentDetail.toasts.verifyFail"), {
        description: err?.message,
      });
    },
  });

  // Send reminder mutation
  const canRemind = invoice?.status === "SENT" || invoice?.status === "UNPAID";

  const reminderMutation = useMutation({
    mutationFn: () => sendOverdueReminder(id!),
    onSuccess: () => {
      toast.success(t("invoice.reminderSent"));
      queryClient.invalidateQueries({ queryKey: ["invoice-detail", id] });
    },
    onError: () => toast.error(t("invoice.reminderFailed")),
  });

  const resendMutation = useMutation({
    mutationFn: (notificationId: string) => resendNotification(notificationId),
    onSuccess: () => {
      toast.success(t("superAdmin.paymentDetail.toasts.resendSuccess"));
      queryClient.invalidateQueries({ queryKey: ["invoice-detail", id] });
    },
    onError: () => toast.error(t("superAdmin.paymentDetail.toasts.resendFail")),
  });

  const handleApprove = () => {
    if (!id) return;

    const data: VerifyPaymentData = {
      approve: true,
      admin_verification_notes: verificationNotes.trim() || undefined,
      admin_confirmation_file: confirmationFile || undefined,
    };

    verifyMutation.mutate({ invoiceId: id, ...data });
    setTimeout(() => setShowApproveDialog(false), 0);
  };

  const handleReject = () => {
    if (!id) return;

    const data: VerifyPaymentData = {
      approve: false,
      admin_verification_notes:
        verificationNotes.trim() || t("superAdmin.paymentDetail.toasts.proofRejected"),
    };

    verifyMutation.mutate({ invoiceId: id, ...data });
    setTimeout(() => setShowRejectDialog(false), 0);
  };

  if (isLoading) {
    return (
      <div className="space-y-5 sm:space-y-6">
        <Card className="rounded-3xl p-6">
          <div className="text-center text-gray-600">
            {t("superAdmin.paymentDetail.loading")}
          </div>
        </Card>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <Card className="rounded-3xl p-6">
        <div className="flex items-center justify-between">
          <div className="text-lg font-semibold">
            {error
              ? `${t("common.error")}: ${
                  error instanceof Error ? error.message : t("superAdmin.common.unknownError")
                }`
              : t("superAdmin.paymentDetail.notFound")}
          </div>
          <Button
            className="rounded-xl"
            variant="outline"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> {t("superAdmin.common.back")}
          </Button>
        </div>
      </Card>
    );
  }

  const paymentProof = invoice.payment_proofs?.[0];
  const canVerify =
    invoice.payment_flow === "PLATFORM_RELAY" &&
    invoice.status === "PENDING_VERIFICATION" &&
    !invoice.verified_at;

  return (
    <div className="space-y-5 sm:space-y-6">
      {/* Header / Actions */}
      <Card className="rounded-3xl p-5 sm:p-6 bg-white/80 ring-1 ring-black/5">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-[18px] sm:text-[20px] font-semibold text-gray-900">
              {t("superAdmin.paymentDetail.title")}
            </h1>
            <p className="text-gray-600 text-sm truncate">
              {invoice.resident_name} • {t("common.unit")} {invoice.unit_code} •{" "}
              {invoice.period}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className={`h-9 px-3 ${QUIET}`}
              onClick={async () => {
                try {
                  const blob = await downloadInvoice(invoice.id);
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `invoice_${invoice.period}_${invoice.unit_code}.pdf`;
                  a.click();
                  URL.revokeObjectURL(url);
                } catch {
                  toast.error(t("invoice.downloadError"));
                }
              }}
            >
              📄 {t("superAdmin.common.pdf")}
            </Button>
            {canRemind && (
              <Button
                variant="outline"
                className="h-9 px-3 border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-50 rounded-xl"
                onClick={() => reminderMutation.mutate()}
                disabled={reminderMutation.isPending}
                title={t("invoice.sendReminder")}
              >
                {reminderMutation.isPending ? (
                  "…"
                ) : (
                  <Bell className="w-4 h-4 mr-1.5" />
                )}
                {t("invoice.sendReminder")}
              </Button>
            )}
            <Button
              variant="outline"
              className={`h-9 px-3 ${QUIET}`}
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" /> {t("superAdmin.common.back")}
            </Button>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Main info */}
        <Card className="rounded-2xl p-4 lg:col-span-2 bg-white/80 ring-1 ring-black/5 space-y-4">
          <SectionTitle>{t("superAdmin.paymentDetail.invoiceSummary")}</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Info label={t("superAdmin.payments.table.resident")} icon={<User2 className="w-4 h-4" />}>
              <div>{invoice.resident_name}</div>
              <div className="text-xs text-gray-500">
                {invoice.resident_email}
              </div>
            </Info>
            <Info label={`${t("common.unit")} / ${t("payments.period")}`} icon={<Building2 className="w-4 h-4" />}>
              {t("common.unit")} {invoice.unit_code} • {invoice.period}
            </Info>
            <Info label={`${t("common.amount")} / ${t("payments.method")}`} icon={<CardIcon className="w-4 h-4" />}>
              <div>{formatCurrency(invoice.total_amount)}
              {invoice.payment_method && ` • ${invoice.payment_method}`}</div>
              {bcvRate && (
                <div className="text-xs text-blue-600">
                  Bs. {(parseFloat(invoice.total_amount) * bcvRate).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  <span className="text-gray-400 ml-1">({t("hoa.payments.bcv.rateInline", { rate: bcvRate.toFixed(2) })})</span>
                </div>
              )}
            </Info>
            {/* This was labelled just "Date" while showing due_date, which reads
                as the date the money arrived. Both dates are shown, labelled. */}
            <Info label={t("superAdmin.paymentDetail.dueDate")} icon={<CalendarDays className="w-4 h-4" />}>
              {formatDate(invoice.due_date, dateLocale, t("common.na"))}
              {invoice.marked_paid_at && (
                <div className="text-xs text-gray-600 mt-0.5">
                  {t("superAdmin.paymentDetail.paidOn", {
                    date: formatDate(invoice.marked_paid_at, dateLocale, t("common.na")),
                  })}
                </div>
              )}
            </Info>
          </div>

          {/* Who confirmed the money arrived. For DIRECT_HOA the resident pays
              the HOA's own account, so the HOA admin recording it IS the
              verification -- there is no platform-side check to display. */}
          {invoice.payment_flow === "DIRECT_HOA" && invoice.marked_paid_at && (
            <div className="mt-4 rounded-2xl bg-emerald-50 ring-1 ring-emerald-200 p-3">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
                <div className="text-sm">
                  <div className="font-medium text-emerald-900">
                    {t("superAdmin.paymentDetail.confirmedByHoa")}
                  </div>
                  <div className="text-emerald-800">
                    {t("superAdmin.paymentDetail.confirmedByHoaDetail", {
                      who: invoice.marked_paid_by_name || invoice.marked_paid_by || t("common.na"),
                      date: formatDate(invoice.marked_paid_at, dateLocale, t("common.na")),
                    })}
                  </div>
                  <div className="text-xs text-emerald-700 mt-1">
                    {t("superAdmin.paymentDetail.directHoaNote")}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Commission and the platform payout account only apply when the
              platform actually receives the money (PLATFORM_RELAY). For
              DIRECT_HOA the resident pays the HOA's account directly and the
              platform never holds funds, so showing "Platform transfers net
              amount to this account" there describes a transfer that never
              happens -- on ~99% of invoices. */}
          {invoice.payment_flow === "PLATFORM_RELAY" && (
            <>
              <CommissionBreakdown invoice={invoice} className="mt-4" />
              {paymentDetails && (
                <div className="mt-4 space-y-3">
                  <PaymentDetailsCard
                    details={paymentDetails.platform_payment_details}
                    title={t("superAdmin.paymentDetail.platformPaymentTitle")}
                    description={t("superAdmin.paymentDetail.platformPaymentDesc")}
                    showVerificationBadge={true}
                  />
                  <PaymentDetailsCard
                    details={paymentDetails.hoa_payment_details}
                    title={t("superAdmin.paymentDetail.hoaPayoutTitle")}
                    description={t("superAdmin.paymentDetail.hoaPayoutDesc")}
                    showVerificationBadge={true}
                  />
                </div>
              )}
            </>
          )}

          {/* Direct-HOA: show the account the resident actually paid into.
              No verification badge -- that flag describes whether the bank
              account has been checked, not whether this payment cleared, and
              next to a settled payment it reads as the latter. */}
          {invoice.payment_flow === "DIRECT_HOA" && paymentDetails?.hoa_payment_details && (
            <div className="mt-4">
              <PaymentDetailsCard
                details={paymentDetails.hoa_payment_details}
                title={t("superAdmin.paymentDetail.hoaReceivingTitle")}
                description={t("superAdmin.paymentDetail.hoaReceivingDesc")}
                showVerificationBadge={false}
              />
            </div>
          )}

          {/* Timeline */}
          <div className="mt-4 rounded-2xl bg-gray-50 ring-1 ring-gray-200 p-3">
            <div className="text-sm font-medium text-gray-900 mb-2">
              {t("superAdmin.paymentDetail.timeline")}
            </div>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">{t("superAdmin.paymentDetail.invoiceCreated")}</span>
                <span className="font-medium">
                  {formatDate(invoice.created_at, dateLocale, t("common.na"))}
                </span>
              </div>
              {invoice.marked_paid_at && (
                <div className="flex justify-between">
                  <span className="text-gray-600">{t("superAdmin.paymentDetail.markedPaid")}</span>
                  <span className="font-medium text-emerald-700">
                    {formatDate(invoice.marked_paid_at, dateLocale, t("common.na"))}
                  </span>
                </div>
              )}
              {invoice.verified_at && (
                <div className="flex justify-between">
                  <span className="text-gray-600">{t("superAdmin.paymentDetail.adminVerified")}</span>
                  <span className="font-medium text-blue-700">
                    {formatDate(invoice.verified_at, dateLocale, t("common.na"))}
                  </span>
                </div>
              )}
              {invoice.finalized_at && (
                <div className="flex justify-between">
                  <span className="text-gray-600">{t("superAdmin.paymentDetail.finalized")}</span>
                  <span className="font-medium text-purple-700">
                    {formatDate(invoice.finalized_at, dateLocale, t("common.na"))}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Payment Proof section */}
          {paymentProof && (
            <div className="mt-4 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 ring-1 ring-blue-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-semibold text-blue-900 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  {t("superAdmin.paymentDetail.proofSubmitted")}
                </div>
                {paymentProof.uploaded_by_name && (
                  <span className="text-xs text-blue-700">
                    {t("superAdmin.paymentDetail.uploadedBy", { name: paymentProof.uploaded_by_name })}
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <MediaPanel
                  label={t("superAdmin.paymentDetail.transactionScreenshot")}
                  path={paymentProof.transaction_screenshot}
                  emptyLabel={t("superAdmin.paymentDetail.noFileProvided")}
                  openLabel={t("superAdmin.paymentDetail.openNewTab")}
                />
                <MediaPanel
                  label={t("superAdmin.paymentDetail.idVerification")}
                  path={paymentProof.id_verification}
                  emptyLabel={t("superAdmin.paymentDetail.noFileProvided")}
                  openLabel={t("superAdmin.paymentDetail.openNewTab")}
                />
              </div>

              {paymentProof.resident_notes && (
                <div className="mt-3 rounded-xl bg-white p-3 ring-1 ring-blue-200">
                  <div className="text-xs font-medium text-gray-700 mb-1">
                    {t("superAdmin.paymentDetail.residentNotes")}
                  </div>
                  <div className="text-sm text-gray-800">
                    {paymentProof.resident_notes}
                  </div>
                </div>
              )}

              {paymentProof.admin_verification_notes && (
                <div className="mt-3 rounded-xl bg-white p-3 ring-1 ring-blue-200">
                  <div className="text-xs font-medium text-gray-700 mb-1">
                    {t("superAdmin.paymentDetail.adminNotes")}
                  </div>
                  <div className="text-sm text-gray-800">
                    {paymentProof.admin_verification_notes}
                  </div>
                </div>
              )}

              {paymentProof.admin_confirmation_file && (
                <div className="mt-3">
                  <MediaPanel
                    label={t("superAdmin.paymentDetail.confirmFile")}
                    path={paymentProof.admin_confirmation_file}
                    emptyLabel={t("superAdmin.paymentDetail.noFileProvided")}
                    openLabel={t("superAdmin.paymentDetail.openNewTab")}
                  />
                </div>
              )}
            </div>
          )}

          {!paymentProof && (
            <div className="mt-4 rounded-2xl bg-amber-50 ring-1 ring-amber-200 p-4">
              <div className="text-sm text-amber-800">
                {t("superAdmin.paymentDetail.noProof")}
              </div>
            </div>
          )}
        </Card>

        {/* Status / Verification actions */}
        <Card className="rounded-2xl p-4 bg-white/80 ring-1 ring-black/5">
          <SectionTitle>{t("superAdmin.paymentDetail.statusTitle")}</SectionTitle>
          <div className="mt-2">
            <StatusBadge status={invoice.status} t={t} />
            {invoice.verified_by_name && (
              <div className="mt-2 text-xs text-gray-600">
                {t("superAdmin.payments.states.markedBy", { name: invoice.verified_by_name })}
              </div>
            )}
          </div>

          {canVerify && paymentProof && (
            <div className="mt-4 space-y-3">
              <Label className="text-sm font-semibold text-gray-900">
                {t("superAdmin.paymentDetail.verification")}
              </Label>

              <div>
                <Label className="text-xs text-gray-700">
                  {t("superAdmin.paymentDetail.notesLabel")}
                </Label>
                <Textarea
                  placeholder={t("superAdmin.payments.dialogs.verificationNotesPlaceholder")}
                  value={verificationNotes}
                  onChange={(e) => setVerificationNotes(e.target.value)}
                  className="mt-1 min-h-[80px]"
                />
              </div>

              <div>
                <Label className="text-xs text-gray-700">
                  {t("superAdmin.paymentDetail.fileLabel")}
                </Label>
                <div className="mt-1">
                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={(e) =>
                      setConfirmationFile(e.target.files?.[0] || null)
                    }
                    className="text-sm"
                  />
                  {confirmationFile && (
                    <div className="mt-1 text-xs text-gray-600">
                      {t("superAdmin.markForward.selected")} {confirmationFile.name}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-2 pt-2">
                <Button
                  onClick={() => setShowApproveDialog(true)}
                  disabled={verifyMutation.isPending}
                  className="w-full rounded-xl text-white bg-emerald-600 hover:bg-emerald-700"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  {t("superAdmin.paymentDetail.approveBtn")}
                </Button>
                <Button
                  onClick={() => setShowRejectDialog(true)}
                  disabled={verifyMutation.isPending}
                  variant="outline"
                  className="w-full rounded-xl text-red-700 border-red-300 hover:bg-red-50"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  {t("superAdmin.paymentDetail.rejectBtn")}
                </Button>
              </div>
            </div>
          )}

          {!canVerify && (
            <div className="mt-4 text-sm text-gray-600">
              {invoice.verified_at
                ? t("superAdmin.paymentDetail.alreadyVerified")
                : t("superAdmin.paymentDetail.notReadyForVerification")}
            </div>
          )}

          {/* Notification log */}
          {invoice.notifications && invoice.notifications.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="text-xs font-medium text-gray-500 mb-2">
                {t("invoice.notifications")}
              </div>
              <div className="space-y-1.5">
                {invoice.notifications.slice(0, 5).map((n: InvoiceNotification) => (
                  <div key={n.id} className="flex items-center gap-2 text-xs">
                    <span className={`inline-block rounded px-1.5 py-0.5 font-semibold ${
                      n.status === "SENT" ? "bg-emerald-100 text-emerald-700" :
                      n.status === "FAILED" ? "bg-red-100 text-red-700" :
                      "bg-gray-100 text-gray-500"
                    }`}>
                      {n.channel === "EMAIL" ? "📧" : "📱"}{" "}
                      {n.status === "SENT"
                        ? t("hoa.notifLog.status.sent")
                        : n.status === "FAILED"
                        ? t("hoa.notifLog.status.failed")
                        : n.status === "MOCKED"
                        ? t("hoa.notifLog.status.mocked")
                        : n.status === "SKIPPED"
                        ? t("hoa.notifLog.status.skipped")
                        : n.status}
                    </span>
                    <span className="text-gray-400 truncate">
                      {n.notification_type === "OVERDUE_REMINDER" ? t("invoice.sendReminder") : t("invoice.notifSent")}
                    </span>
                    {n.status === "FAILED" && (
                      <button
                        onClick={() => resendMutation.mutate(n.id)}
                        disabled={resendMutation.isPending}
                        className="ml-1 rounded px-1.5 py-0.5 text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50 border border-blue-200"
                        title={t("hoa.notifLog.retryNotification")}
                      >
                        ↺ {t("superAdmin.common.resend")}
                      </button>
                    )}
                    <span className="text-gray-300 ml-auto whitespace-nowrap">
                      {new Date(n.sent_at).toLocaleDateString(dateLocale)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 pt-4 border-t border-gray-200">
            <Button
              variant="outline"
              className={`w-full ${QUIET}`}
              onClick={() => navigate("/super-admin/payments")}
            >
              {t("superAdmin.paymentDetail.backToInbox")}
            </Button>
          </div>
        </Card>
      </div>

      {/* Approve confirmation */}
      {showApproveDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold mb-2">{t("superAdmin.paymentDetail.approveTitle")}</h3>
            <p className="text-sm text-gray-600 mb-4">
              {t("superAdmin.paymentDetail.approveDesc")}
            </p>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                className={QUIET}
                onClick={() => setShowApproveDialog(false)}
              >
                {t("superAdmin.common.cancel")}
              </Button>
              <Button
                onClick={handleApprove}
                disabled={verifyMutation.isPending}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {verifyMutation.isPending ? t("superAdmin.payments.actions.approving") : t("superAdmin.payments.actions.confirmApproval")}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Reject confirmation */}
      {showRejectDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold mb-2">{t("superAdmin.paymentDetail.rejectTitle")}</h3>
            <p className="text-sm text-gray-600 mb-4">
              {t("superAdmin.paymentDetail.rejectDesc")}
            </p>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                className={QUIET}
                onClick={() => setShowRejectDialog(false)}
              >
                {t("superAdmin.common.cancel")}
              </Button>
              <Button
                onClick={handleReject}
                disabled={verifyMutation.isPending}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {verifyMutation.isPending ? t("superAdmin.payments.actions.rejecting") : t("superAdmin.payments.actions.confirmRejection")}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

/* --- Helpers --- */

/**
 * Renders one uploaded file, or a placeholder when there isn't one.
 *
 * The API returns null for empty file fields, and most payment proofs have no
 * ID document at all (255 of 277 in production), so every one of these has to
 * tolerate an absent file. Previously each block called `.startsWith()`
 * directly on the path, which threw and blanked the entire page.
 */
function MediaPanel({
  label,
  path,
  emptyLabel,
  openLabel,
}: {
  label: string;
  path?: string | null;
  emptyLabel: string;
  openLabel: string;
}) {
  const url = mediaUrl(path);

  return (
    <div className="rounded-xl bg-white p-3 ring-1 ring-blue-200">
      <div className="text-xs font-medium text-gray-700 mb-2">{label}</div>
      {url ? (
        <>
          <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-100 border mb-2">
            <img
              src={url}
              alt={label}
              className="w-full h-full object-contain cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => openMedia(path)}
            />
          </div>
          <button
            type="button"
            onClick={() => openMedia(path)}
            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            <Eye className="w-4 h-4" />
            {openLabel}
          </button>
        </>
      ) : (
        <div className="flex aspect-video items-center justify-center rounded-lg border border-dashed bg-gray-50 text-xs text-gray-500">
          {emptyLabel}
        </div>
      )}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <div className="text-sm font-semibold text-gray-900">{children}</div>;
}

function Info({
  label,
  children,
  icon,
}: {
  label: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl bg-white/80 ring-1 ring-black/5 p-3">
      <div className="text-xs font-medium text-gray-500 flex items-center gap-1.5">
        {icon} {label}
      </div>
      <div className="mt-1 text-sm text-gray-900 break-words">{children}</div>
    </div>
  );
}

function StatusBadge({
  status,
  t,
}: {
  status: InvoiceStatus;
  t: (key: string, params?: Record<string, string | number>) => string;
}) {
  if (status === "PENDING_VERIFICATION") {
    return (
      <Badge className="bg-amber-100 text-amber-900 ring-1 ring-amber-300">
        {t("superAdmin.payments.status.pendingVerification")}
      </Badge>
    );
  }
  if (status === "PAID") {
    return (
      <Badge className="bg-emerald-100 text-emerald-900 ring-1 ring-emerald-300">
        {t("superAdmin.payments.status.paid")}
      </Badge>
    );
  }
  if (status === "UNPAID") {
    return (
      <Badge className="bg-red-100 text-red-900 ring-1 ring-red-300">
        {t("superAdmin.payments.status.unpaid")}
      </Badge>
    );
  }
  if (status === "SENT") {
    return (
      <Badge className="bg-blue-100 text-blue-900 ring-1 ring-blue-300">
        {t("superAdmin.payments.status.sent")}
      </Badge>
    );
  }
  if (status === "DRAFT") {
    return (
      <Badge className="bg-gray-100 text-gray-900 ring-1 ring-gray-300">
        {t("superAdmin.payments.status.draft")}
      </Badge>
    );
  }
  if (status === "CANCELLED") {
    return (
      <Badge className="bg-gray-100 text-gray-900 ring-1 ring-gray-300">
        {t("superAdmin.payments.status.cancelled")}
      </Badge>
    );
  }
  if (status === "PENDING_HOA_TRANSFER") {
    return (
      <Badge className="bg-amber-100 text-amber-900 ring-1 ring-amber-300">
        {t("hoa.payments.status.pendingHoaTransfer")}
      </Badge>
    );
  }
  if (status === "PARTIALLY_PAID") {
    return (
      <Badge className="bg-amber-100 text-amber-900 ring-1 ring-amber-300">
        {t("hoa.payments.status.partiallyPaid")}
      </Badge>
    );
  }
  if (status === "SUBMITTED_TO_HOA") {
    return (
      <Badge className="bg-purple-100 text-purple-900 ring-1 ring-purple-300">
        {t("superAdmin.payments.states.waitingForHoa")}
      </Badge>
    );
  }
  return (
    <Badge className="bg-gray-100 text-gray-900 ring-1 ring-gray-300">
      {t("common.unknown")}
    </Badge>
  );
}
